// TODO: X2-14438 Remove this plugin and its `dayjs.extend` once supported browsers and Node ship tzdata 2026e+
// A copy lives in x2-checkout (src/helpers/fixedOffsetZones.ts). Make every change there too.
/**
 * Corrects dayjs timezone conversions for zones that stopped changing their clocks, in browsers whose tzdata is
 * older than that change.
 *
 * dayjs has no timezone data of its own: its timezone plugin asks the browser (`Intl.DateTimeFormat`) for every
 * offset. From 2026-11-01 British Columbia stays on UTC-7, Alberta on UTC-6 and Manitoba on UTC-5, but a browser
 * with older tzdata still falls back an hour. For a 4pm booking on Sat Nov 7 (23:00Z):
 *
 *   up-to-date browser  dayjs("2026-11-07T23:00:00Z").tz("America/Vancouver")  → 4:00 PM -07:00
 *   stale browser       dayjs("2026-11-07T23:00:00Z").tz("America/Vancouver")  → 3:00 PM -08:00
 *
 * With this plugin the stale browser gets 4:00 PM -07:00 as well. It changes nothing when:
 *  - the browser's tzdata already knows the change (checked once, when the plugin is registered)
 *  - the zone isn't listed in `fixedOffsetRules`, e.g. America/New_York
 *  - the time is before the change, e.g. 2025-11-15 in Vancouver stays -08:00
 *
 * The one exception to the last point is the hour stale tzdata repeats on the day of the change, see `parseClockTime`.
 */
import { Dayjs, PluginFunc } from "dayjs";

// dayjs's private locale and plugin fields, which the timezone plugin reads
type ZonedDayjs = Dayjs & { $L: string; $x: { $timezone?: string } };

interface FixedOffsetRule {
    /** IANA names that follow the rule, including legacy aliases */
    timezones: string[];
    /** When the zone stopped changing clocks (epoch ms). Earlier times keep the browser's historical offsets */
    start: number;
    /** Minutes from UTC from `start` onwards */
    offset: number;
    /** What up-to-date tzdata calls the zone from `start`, for the `z` / `zzz` format tokens */
    names: { short: string; long: string };
}

/**
 * One rule per tzdata release that added the change, because the browser check runs per rule: a browser can have
 * 2026b but not 2026e, and know British Columbia's change but not Manitoba's.
 */
export const fixedOffsetRules: FixedOffsetRule[] = [
    // British Columbia skips the fall-back at 2am PDT on 2026-11-01 (09:00Z) and stays on UTC-7 (tzdata 2026b)
    {
        timezones: ["America/Vancouver", "Canada/Pacific"],
        start: Date.parse("2026-11-01T09:00:00Z"),
        offset: -7 * 60,
        names: { short: "PDT", long: "Pacific Daylight Time" },
    },
    // Alberta and Yellowknife skip the fall-back at 2am MDT on 2026-11-01 (08:00Z) and stay on UTC-6 (tzdata 2026c)
    {
        timezones: ["America/Edmonton", "America/Yellowknife", "Canada/Mountain"],
        start: Date.parse("2026-11-01T08:00:00Z"),
        offset: -6 * 60,
        names: { short: "MDT", long: "Mountain Daylight Time" },
    },
    // Manitoba skips the fall-back at 2am CDT on 2026-11-01 (07:00Z) and stays on UTC-5 (tzdata 2026e)
    {
        timezones: ["America/Winnipeg", "Canada/Central", "America/Rainy_River"],
        start: Date.parse("2026-11-01T07:00:00Z"),
        offset: -5 * 60,
        names: { short: "CDT", long: "Central Daylight Time" },
    },
];

const MINUTE = 60 * 1000;
const WEEK = 7 * 24 * 60 * MINUTE;
const CLOCK_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSS";

/**
 * Asks the browser for the hour in the zone a week after the change. For Vancouver that is 2026-11-08T09:00Z, which
 * up-to-date tzdata shows as 2am (UTC-7) and stale tzdata as 1am (UTC-8).
 */
export const doesBrowserHaveUpdatedTimezoneData = ({ timezones, start, offset }: FixedOffsetRule) => {
    const probe = start + WEEK;
    try {
        const hour = new Intl.DateTimeFormat("en-US", { timeZone: timezones[0], hour: "numeric", hourCycle: "h23" })
            .formatToParts(new Date(probe))
            .find((part) => part.type === "hour")?.value;
        return Number(hour) === new Date(probe + offset * MINUTE).getUTCHours();
    } catch (error) {
        // An unknown zone has nothing to correct. For any other failure the rule applies, since it gives the right
        // offset either way, and rethrowing would stop the app loading.
        return error instanceof RangeError;
    }
};

/** Whether the browser misses any of the changes, which is when this plugin corrects its times */
export const isBrowserTimezoneDataOutdated = () =>
    fixedOffsetRules.some((rule) => !doesBrowserHaveUpdatedTimezoneData(rule));

interface ParseClockTimeOptions {
    clockTime: string;
    rule: FixedOffsetRule;
    timezone: string;
    parseWithBrowserRules: () => Dayjs;
    parseFormat?: string;
    locale?: string;
}

/** `rules` replaces the browser check, so tests can apply the rules on any runtime */
export const fixedOffsetZones: PluginFunc<{ rules?: FixedOffsetRule[] }> = (option, dayjsClass, dayjsFactory) => {
    const rules = option?.rules ?? fixedOffsetRules.filter((rule) => !doesBrowserHaveUpdatedTimezoneData(rule));
    if (rules.length === 0) {
        return;
    }

    // The timezone plugin's own functions, which read every offset from the browser's tzdata
    const browserTzFactory = dayjsFactory.tz;
    const browserTz = dayjsClass.prototype.tz;
    const browserOffsetName = dayjsClass.prototype.offsetName;
    let defaultTimezone = (browserTzFactory() as ZonedDayjs).$x.$timezone;

    const findRule = (timezone?: string) => rules.find((rule) => !!timezone && rule.timezones.includes(timezone));

    // The timestamp at which a clock set to `offset` shows `clockTime`, e.g. 2026-11-07 16:00 at -07:00 → 23:00Z
    const clockTimeToTimestamp = (clockTime: string, offset: number, parseFormat?: string) =>
        dayjsFactory.utc(clockTime, parseFormat).valueOf() - offset * MINUTE;

    // The same object the timezone plugin would build, with the given offset instead of the browser's
    const createZonedDate = (timestamp: number, offset: number, timezone: string, locale?: string) => {
        const date = dayjsFactory(timestamp)
            .utcOffset(offset)
            .locale(locale ?? dayjsFactory.locale()) as ZonedDayjs;
        date.$x.$timezone = timezone; // Keeps `startOf`, `endOf` and `offsetName` working in the zone
        return date;
    };

    // The offset the zone had just before the change, e.g. -07:00 (PDT) for Vancouver
    const getOffsetBeforeChange = (rule: FixedOffsetRule, timezone: string) =>
        browserTz.call(dayjsFactory(rule.start - 1), timezone).utcOffset();

    /**
     * Finds the timestamp at which the zone's clock shows `clockTime`:
     *  - from the change onwards, with the rule's offset
     *      2026-11-07 16:00 in Vancouver → 2026-11-07T23:00Z (-07:00)
     *  - before the change, with the browser's tzdata, which is still correct for those dates
     *      2025-11-15 16:00 in Vancouver → 2025-11-16T00:00Z (-08:00)
     *  - except for the hour stale tzdata repeats. To it, 1:30 AM on 2026-11-01 in Vancouver is both 08:30Z (PDT)
     *    and, after the fall-back that no longer happens, 09:30Z (PST). dayjs picks between the two with today's
     *    offset, so a stale browser picks 09:30Z once it's past the change. The zone never reached that second
     *    1:30, so it gets the offset from just before the change:
     *      2026-11-01 01:30 in Vancouver → 2026-11-01T08:30Z (-07:00)
     */
    const parseClockTime = ({
        clockTime,
        rule,
        timezone,
        parseWithBrowserRules,
        parseFormat,
        locale,
    }: ParseClockTimeOptions) => {
        const timestamp = clockTimeToTimestamp(clockTime, rule.offset, parseFormat);
        if (timestamp >= rule.start) {
            return createZonedDate(timestamp, rule.offset, timezone, locale);
        }

        const date = parseWithBrowserRules();
        if (!date.isValid() || date.valueOf() < rule.start) {
            return date;
        }

        const offsetBeforeChange = getOffsetBeforeChange(rule, timezone);
        const timestampBeforeChange = clockTimeToTimestamp(clockTime, offsetBeforeChange, parseFormat);
        return createZonedDate(timestampBeforeChange, offsetBeforeChange, timezone, locale);
    };

    /**
     * Converting a timestamp into a zone:
     *   dayjs("2026-11-07T23:00:00Z").tz("America/Vancouver")        → 2026-11-07T16:00:00-07:00
     *   dayjs("2025-11-15T00:00:00Z").tz("America/Vancouver")        → 2025-11-14T16:00:00-08:00  (before the change)
     * With `keepLocalTime`, the clock time is kept and the timestamp moves, see `parseClockTime`:
     *   dayjs.utc("2026-11-07T16:00:00Z").tz("America/Vancouver", true) → 2026-11-07T16:00:00-07:00
     */
    // With no zone and no default, dayjs uses the computer's own zone, which may be one of the rules' zones
    dayjsClass.prototype.tz = function (
        this: Dayjs,
        timezone = defaultTimezone ?? browserTzFactory.guess(),
        keepLocalTime = false,
    ) {
        const { $L: locale } = this as ZonedDayjs;
        const rule = findRule(timezone);
        if (rule && keepLocalTime) {
            return parseClockTime({
                clockTime: this.format(CLOCK_TIME_FORMAT),
                rule,
                timezone,
                parseWithBrowserRules: () => browserTz.call(this, timezone, true),
                locale,
            });
        }

        if (rule && this.valueOf() >= rule.start) {
            return createZonedDate(this.valueOf(), rule.offset, timezone, locale);
        }

        return browserTz.call(this, timezone, keepLocalTime);
    };

    /**
     * Parsing a clock time in a zone, e.g. what a seller picks, see `parseClockTime`:
     *   dayjs.tz("2026-11-07 16:00", "America/Vancouver") → 2026-11-07T23:00:00Z  (stale browsers give 2026-11-08T00:00Z)
     *   dayjs.tz("2026-11-07 16:00")                      → same, using the zone from `dayjs.tz.setDefault`
     */
    const tzFactoryWithRules = (input?: string | number | Date | Dayjs, arg1?: string, arg2?: string) => {
        const parseFormat = arg2 && arg1;
        const timezone = arg2 || arg1 || defaultTimezone || browserTzFactory.guess();
        // Non-string input ends up in the `.tz()` override above
        const parseWithBrowserRules = () =>
            parseFormat ? browserTzFactory(input, parseFormat, arg2) : browserTzFactory(input, arg1);

        const rule = findRule(timezone);
        if (rule && typeof input === "string") {
            return parseClockTime({ clockTime: input, rule, timezone, parseWithBrowserRules, parseFormat });
        }

        return parseWithBrowserRules();
    };

    /**
     * The zone name for the `z` / `zzz` format tokens:
     *   dayjs("2026-11-07T23:00:00Z").tz("America/Vancouver").format("z") → "PDT"  (stale browsers say "PST")
     */
    dayjsClass.prototype.offsetName = function (this: Dayjs, type?: "short" | "long") {
        const rule = findRule((this as ZonedDayjs).$x.$timezone);
        if (rule && this.valueOf() >= rule.start) {
            return rule.names[type ?? "short"];
        }

        return browserOffsetName.call(this, type);
    };

    Object.assign(dayjsFactory, {
        tz: Object.assign(tzFactoryWithRules as typeof browserTzFactory, {
            guess: browserTzFactory.guess,
            setDefault: (timezone?: string) => {
                defaultTimezone = timezone;
                browserTzFactory.setDefault(timezone as string);
            },
        }),
    });
};
